import { ReactNode } from "react";
import { Button, Flex, TextField } from "@radix-ui/themes";
import { normalizeAccent } from "@/shared/galleryTheme";

interface Props {
  id?: string;
  /** Names the swatch for screen readers — the well itself has no visible label. */
  label: string;
  /** `#RRGGBB`, or "" for unset. Kept as typed so a half-entered hex survives. */
  value: string;
  /** The hex shown behind an empty field: what will be used if nothing is set.
   *  "" when there is no such colour — an optional field with no inherited
   *  value behind it. */
  placeholder: string;
  /** Where the picker opens when neither the value nor the placeholder is a
   *  usable colour. Somewhere in the neighbourhood beats black. */
  fallback?: string;
  onChange: (value: string) => void;
  /** Omit to hide the clear button entirely — some fields are not optional. */
  onClear?: () => void;
  clearLabel?: ReactNode;
  size?: "1" | "2" | "3";
}

// A hex colour, entered two ways: the OS colour picker for choosing one, and a
// text field for pasting the value a designer sent over. Both write the same
// string, so neither is the source of truth.
export default function ColorField({
  id,
  label,
  value,
  placeholder,
  fallback = "#000000",
  onChange,
  onClear,
  clearLabel = "Clear",
  size = "2",
}: Props) {
  // <input type="color"> only accepts a full #RRGGBB and silently resets to
  // black on anything else, so it shows the placeholder until what has been
  // typed is a whole colour — mid-typing must not look like a choice.
  const well =
    normalizeAccent(value) ??
    normalizeAccent(placeholder) ??
    normalizeAccent(fallback) ??
    "#000000";

  return (
    <Flex gap="2" align="center">
      <input
        type="color"
        aria-label={label}
        value={well}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        style={{
          width: "2.75rem",
          height: "2rem",
          padding: 0,
          border: "1px solid var(--gray-6)",
          background: "none",
          cursor: "pointer",
        }}
      />
      <TextField.Root
        id={id}
        size={size}
        value={value}
        placeholder={placeholder}
        maxLength={7}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: "9rem", fontFamily: "var(--code-font-family)" }}
      />
      {onClear && value !== "" && (
        <Button
          type="button"
          variant="ghost"
          color="gray"
          size="1"
          onClick={onClear}
        >
          {clearLabel}
        </Button>
      )}
    </Flex>
  );
}
